// @ts-nocheck
import { AddCircleOutline } from '@mui/icons-material'
import { Autocomplete, Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AppTextField } from 'src/components/AppTextField'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { CaptionedCell } from 'src/components/table/CaptionedCell'
import BasicDatePicker from 'src/components/DateTimePicker'
import { getTranslatedText } from 'src/utils'
import { TaskTypes } from 'src/enums'
import { IDropdownDto } from 'src/generated-types/api-result'
import { SchemaType } from 'src/generated-types/form-schema'
import { IMainComponent } from 'src/generated-types/main-component'
import { IResource } from 'src/generated-types/resource'
import TemporaryVesselTable from 'src/screens/localities/tasks/TemporaryVesselTable'
import { Flex } from 'src/styles/flexComponent'
import { Padding } from 'src/styles/paddingComponent'
import { useGetCustomerUsers } from '../../../../api/customer'
import { useGetFormSchemasDropdown } from '../../../../api/form-schema'
import { useGetCustomerLocalities } from '../../../../api/localities'
import { FormFileCoreUpload, FormSelectField, FormTextField } from '../../../../components/FormCore'
import { ILocality } from '../../../../generated-types'
import { EdgeCase, getUserDetail, UserDetail } from '../../../../utils/getUserDetails'
import { taskIntervalList } from './task-constants'
import { useGetEquipmentForWorkOrder } from 'src/api/scheduledTask'

interface InformationFormProps {
  customerId: any
  localityId?: EntityId
  formType: TaskTypes
  isDurationDisabled: boolean
  setIisDurationDisabled: React.Dispatch<React.SetStateAction<boolean>>
  vessels?: IResource[]
  setVessels?: React.Dispatch<React.SetStateAction<IResource[]>>
  onAssignVessel?: () => void
  selectedEquipments?: IMainComponent[]
  setSelectedEquipments?: React.Dispatch<React.SetStateAction<IMainComponent[]>>
}

const InformationForm: React.FC<InformationFormProps> = ({
  customerId,
  localityId,
  formType,
  isDurationDisabled,
  setIisDurationDisabled,
  vessels,
  setVessels,
  onAssignVessel,
  selectedEquipments,
  setSelectedEquipments,
}) => {
  const { t: translate } = useTranslation(['locality', 'common'])
  const { setValue, setFocus, clearErrors } = useFormContext()
  const [checkValues, setCheckValues] = useState({ startDate: false, duration: false })

  const { data: customerUsers } = useGetCustomerUsers(customerId as EntityId)
  const { data: localities } = useGetCustomerLocalities({ clientId: customerId as EntityId })
  const { data: formSchemas } = useGetFormSchemasDropdown(customerId as EntityId, SchemaType.FormSchema)
  const { data: equipments } = useGetEquipmentForWorkOrder(localityId as EntityId, undefined, {
    enabled: !!localityId,
  })

  const currentLocality = localities?.find(l => l.id == localityId)

  useEffect(() => {
    if (currentLocality) {
      setValue('localityIds', currentLocality ? [{ ...currentLocality }] : [])
    }
  }, [currentLocality])

  return (
    <>
      <FormTextField name="title" label={translate('title', { ns: 'common' })} />
      <Flex.Row>
        <Flex.Column width={'50%'}>
          <BasicDatePicker
            name="startDate"
            label={translate('startDate', { ns: 'common' })}
            onChange={val => {
              setValue('startDate', val)
              clearErrors('startDate')
              setCheckValues(prev => ({ ...prev, startDate: val ? true : false }))
            }}
          />
        </Flex.Column>
        <Flex.Column width={'50%'}>
          {formType == TaskTypes.Repeatable && (
            <BasicDatePicker
              name="endDate"
              label={translate('endDate', { ns: 'common' })}
              onChange={val => {
                setValue('endDate', val)
                clearErrors('endDate')
              }}
            />
          )}
        </Flex.Column>
      </Flex.Row>
      <Flex.Row>
        {formType == TaskTypes.Repeatable && (
          <Flex.Column width={'50%'}>
            <FormSelectField
              fullWidth
              name="interval"
              data={taskIntervalList}
              disablePortal={false}
              label={translate('interval', { ns: 'task' })}
              getOptionLabel={option => option.option}
              renderOption={(props: any, option) => (
                <Box {...props} key={option.value}>
                  {option.option}
                </Box>
              )}
              clearOnBlur={false}
              clearOnEscape={false}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              onChange={(eve, value) => {
                setValue('interval', value)
                clearErrors('interval')
                if (value == taskIntervalList[0]) {
                  setIisDurationDisabled(true)
                  setValue('duration', '1')
                  setFocus('duration')
                } else {
                  setIisDurationDisabled(false)
                }
              }}
            />
          </Flex.Column>
        )}
        <Flex.Column width={'50%'}>
          <FormTextField
            type="number"
            name="duration"
            label={translate('duration', { ns: 'task' })}
            InputProps={{
              readOnly: isDurationDisabled,
              onChange: e => {
                setValue('duration', e.target.value)
                setCheckValues(prev => ({ ...prev, duration: !!e.target.value }))
              },
            }}
            focused={isDurationDisabled}
          />
        </Flex.Column>
      </Flex.Row>
      <FormSelectField
        name="localityIds"
        data={localities || []}
        label={translate('localities', { ns: 'common' })}
        getOptionLabel={(option: ILocality) => option.name}
        renderOption={(props: any, option) => (
          <Box {...props} key={option.id}>
            {option.name}
          </Box>
        )}
        multiple
        disableCloseOnSelect
        defaultValue={currentLocality ? [{ ...currentLocality }] : []}
        clearOnBlur={false}
        clearOnEscape={false}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        disabled={!!localityId}
      />
      <FormSelectField
        name="responsiblePerson"
        data={customerUsers!}
        label={translate('responsibleUser', { ns: 'locality' })}
        getOptionLabel={option => getUserDetail(option, UserDetail.NameAndEmail, EdgeCase.ReturnEmailForNewUser)}
        renderOption={(props: any, option) => (
          <Box {...props} key={option.id}>
            {getUserDetail(option, UserDetail.NameAndEmail, EdgeCase.ReturnEmailForNewUser)}
          </Box>
        )}
        clearOnBlur={false}
        clearOnEscape={false}
        isOptionEqualToValue={(o, v) => o.id === v.id}
      />
      <FormTextField name="description" label={translate('description', { ns: 'common' })} multiline rows={4} />
      <FormSelectField
        name="formSchemaIds"
        data={formSchemas || []}
        defaultValue={[]}
        label={translate('reports', { ns: 'common' })}
        getOptionLabel={(option: IDropdownDto) => option.name}
        renderOption={(props: any, option) => (
          <Box {...props} key={option.id}>
            {option.name}
          </Box>
        )}
        multiple
        disableCloseOnSelect
        clearOnBlur={false}
        clearOnEscape={false}
        isOptionEqualToValue={(o, v) => o.id === v.id}
      />
      {formType == TaskTypes.Once && localityId && (
        <Padding.p8>
          <CommonPageLayout
            titleSection={{ subtitle: translate('resources', { ns: 'task' }), subtitleFontSize: '16px' }}
            topAction={[
              {
                buttonText: translate('resources', { ns: 'task' }),
                buttonIcon: <AddCircleOutline />,
                onClick: onAssignVessel,
                variant: 'outlined',
                shadowWeight: 0,
                disabled: !(checkValues.startDate && checkValues.duration),
              },
            ]}
          >
            <TemporaryVesselTable vessels={vessels ?? []} setVessels={setVessels} />
          </CommonPageLayout>
        </Padding.p8>
      )}
      {localityId && (
        <Autocomplete<IMainComponent, true>
          options={(equipments || []) as IMainComponent[]}
          autoHighlight
          getOptionLabel={o => `${getTranslatedText(o.typeName)} | ${o.serialNumber}` || ''}
          renderOption={(props, o) => (
            <Box component="li" {...props} key={o.id}>
              <CaptionedCell primary={getTranslatedText(o.typeName)} caption={o.serialNumber} />
            </Box>
          )}
          multiple
          disableCloseOnSelect
          value={selectedEquipments || []}
          renderInput={params => (
            <AppTextField
              {...params}
              label={
                formType == TaskTypes.Once
                  ? translate('equipment', { ns: 'common' })
                  : translate('default-equipment', { ns: 'task' })
              }
              inputProps={{ ...params.inputProps }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => {
            setSelectedEquipments?.(value)
          }}
        />
      )}
      <FormFileCoreUpload
        label={translate('fileName', { ns: 'common' })}
        name="fileIds"
        mapFileKey="id"
        height="150px"
        multiple
      />
    </>
  )
}

export default InformationForm
